import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useQuery } from 'react-apollo';

import {
  accountCardConfigurationValidate,
  getDefaultCardSideIds,
} from '../../../../../../../../../../services/cards/views/AccountCardConfigurator/helpers';
import AccountCard from '../../../../../../../../../../services/cards/components/AccountCard';
import Spinner from '../../../../../../../../../../components/global/Spinner';
import useAccountCardConfigure from '../../../../../../../../../../services/cards/views/AccountCardConfigurator/useAccountCardConfigure';
import getGlobalLayoutCard from '../../../../../queries/getGlobalLayoutCard';
import LayoutSelector from './components/LayoutSelector';
import { ACCOUNT_CARD_TYPE } from '../../../../../../../../../../services/cards/constants';
import IntroSlideLabel from '../../../../../../MemoryBookAlbum/components/MemoryBookIntroSlide/components/IntroSlideLabel';
import { isMobile } from '../../../../../../../../../../util/device-detector';

const propTypes = {
  className: PropTypes.string,
  accountCardId: PropTypes.string,
  albumId: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired,
  onAutosave: PropTypes.func.isRequired,
  tickerTape: PropTypes.node,
  slideBackgroundColor: PropTypes.string,
  slideBackgroundImage: PropTypes.shape({
    url: PropTypes.string,
  }),
};

const IntroLayoutConfigurator = ({
  onAutosave,
  onDelete,
  accountCardId,
  albumId,
  tickerTape,
  className,
  slideBackgroundColor,
  slideBackgroundImage,
}) => {
  const classes = classNames({
    [className]: !!className,
    'intro-layout-configurator': true,
  });

  const { data: { globalLayoutCard } = {}, loading: loadingDefaultLayoutCard } =
    useQuery(getGlobalLayoutCard, {
      fetchPolicy: 'cache-first',
    });

  const {
    accountCard,
    onCardChange,
    onElementConfigurationChange,
    onAccountCardDelete,
    cardElementLoading,
  } = useAccountCardConfigure({
    accountCardId,
    presetCardElementConfigurationInputs: [],
    storeFetchedAccountCard: true,
  });

  // Overall we need a cleanup process for the card if it did not end up entirely configured.
  // This process will entail deleting it once the user completely unmounts the component, which
  // shall happen after, for example, changing to another editor tab.
  //
  // What will happen is the following:
  // - This use effect will only trigger once, on full unmount, and will trigger an unmount event
  // - The next effect will regenerate a check function which will change based off the current status of the card
  useEffect(() => {
    return () => {
      const e = new Event('componentUnmount');
      document.dispatchEvent(e);
    };
  }, []);

  useEffect(() => {
    const onUnmount = () => {
      if (!accountCardConfigurationValidate(accountCard)) {
        onAccountCardDelete();
      }
    };

    document.addEventListener('componentUnmount', onUnmount);
    return () => {
      // This is done whenever value of somethingChanged changes
      document.removeEventListener('componentUnmount', onUnmount);
    };
  }, [accountCard]);

  useEffect(() => {
    // If the accountCard has been entirely configured, we want to save it.
    // This shall only happen in the event that either, we have no existent card,
    // or we have updated the card to a new one by selecting a new template.
    if (!accountCardConfigurationValidate(accountCard)) {
      return;
    }

    if (!accountCardId || accountCard.id !== accountCardId) {
      onAutosave({ accountCard });
    }
  }, [accountCard]);

  useEffect(() => {
    // Create the AccountCard if we're starting from scratch.
    if (globalLayoutCard && !accountCardId) {
      onCardChange({
        card_id: globalLayoutCard.id,
        type: ACCOUNT_CARD_TYPE.AUTO_LAYOUT,
        ...getDefaultCardSideIds(globalLayoutCard),
      });
    }
  }, [globalLayoutCard]);

  return (
    <div className={classes}>
      {accountCard && !loadingDefaultLayoutCard ? (
        <Fragment>
          <div
            className="relative-container"
            style={{
              backgroundColor: slideBackgroundColor,
              backgroundImage: slideBackgroundImage
                ? `url(${slideBackgroundImage.url})`
                : '',
            }}
          >
            <IntroSlideLabel albumId={albumId} editable />
            <AccountCard
              animatable={false}
              disablePhotoWatermarkOverlay
              className="account-card-intro-layout-configurator"
              onElementConfigurationChange={onElementConfigurationChange}
              accountCard={accountCard}
              cardElementLoading={cardElementLoading}
              photosContext={{
                album_id: albumId,
                is_all_photos: true,
                force_entitlement: true,
                uploads_disabled: false,
              }}
            />
            {tickerTape}
          </div>
          {!accountCardConfigurationValidate(accountCard) && (
            <p className="text-center text-light mt-1 mb-0">
              {isMobile() ? 'Tap' : 'Click'} on the photo slots to select a
              photo
            </p>
          )}
        </Fragment>
      ) : (
        <div className="relative-container">
          <Spinner className="m-auto" />
        </div>
      )}
      <LayoutSelector
        cardSideId={accountCard?.account_card_sides?.[0]?.card_side?.id}
        onCancel={() => {
          // We're not editing, but creating. If we go back here, delete.
          if (!accountCardId) {
            onAccountCardDelete();
          } else {
            // X function will work as a delete button if we have an accountCard.
            onDelete();
          }
        }}
        onCardSideSelect={({ cardSide }) =>
          onCardChange(
            {
              card_id: globalLayoutCard.id,
              type: ACCOUNT_CARD_TYPE.AUTO_LAYOUT,
              front_card_side_id: cardSide.id,
            },
            accountCardId !== accountCard?.id,
          )
        }
        cardSides={
          globalLayoutCard?.card_side_groups?.records?.[0]?.card_sides?.records
        }
      />
    </div>
  );
};

IntroLayoutConfigurator.propTypes = propTypes;

export default IntroLayoutConfigurator;
